import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { buildQueryString } from '../../utils/http-utils';

export interface ApiSuccessResponse<T> {
  status: 'success' | string;
  message: string;
  data: T;
}

export interface ClientTemplateField {
  id: string | number;
  template_id: string | number;
  field_name: string;
  display_name: string;
  field_type: string;
  data_type: string;
  is_mandatory: boolean;
  is_visible: boolean;
  is_custom_field: boolean;
  tab_id: string | number;
  sub_tab_id: number;
  display_order: number;
  validation_rules: string | null;
  help_text: string | null;
  default_value: unknown;
  is_active: boolean;
  is_deleted: boolean;
  options?: unknown[];
  show_if?: Record<string, unknown>;
  width?: number;
}

export interface ClientTemplatePayload {
  template?: Record<string, unknown>;
  fields?: ClientTemplateField[];
}

export interface ClientRecord {
  id?: number | string;
  client?: Record<string, unknown>;
  client_details?: Record<string, unknown> | Record<string, unknown>[];
  client_tax_info?: Record<string, unknown> | Record<string, unknown>[];
  client_address?: Record<string, unknown> | Record<string, unknown>[];
  client_financial_details?: Record<string, unknown>;
  client_tax_information?: Record<string, unknown>;
  client_shipping_address?: Record<string, unknown>;
  client_contact?: Record<string, unknown> | Record<string, unknown>[];
  client_files?: Record<string, unknown> | Record<string, unknown>[];
  client_phone_call?: Record<string, unknown> | Record<string, unknown>[];
  client_user_note?: Record<string, unknown> | Record<string, unknown>[];
  client_basic_details?: Record<string, unknown> | Record<string, unknown>[];
  client_classification?: Record<string, unknown> | Record<string, unknown>[];
  client_subsidiary?: Record<string, unknown> | Record<string, unknown>[];
  client_workflow_and_automation?: Record<string, unknown> | Record<string, unknown>[];
  custom_fields?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ClientSubmissionPayload {
  client: Record<string, unknown>;
  client_details?: Record<string, unknown> | Record<string, unknown>[];
  client_tax_info?: Record<string, unknown> | Record<string, unknown>[];
  client_address?: Record<string, unknown> | Record<string, unknown>[];
  client_contact?: Record<string, unknown> | Record<string, unknown>[];
  client_files?: Record<string, unknown> | Record<string, unknown>[];
  client_phone_call?: Record<string, unknown> | Record<string, unknown>[];
  client_user_note?: Record<string, unknown> | Record<string, unknown>[];
  client_basic_details?: Record<string, unknown> | Record<string, unknown>[];
  client_classification?: Record<string, unknown> | Record<string, unknown>[];
  client_subsidiary?: Record<string, unknown> | Record<string, unknown>[];
  client_workflow_and_automation?: Record<string, unknown> | Record<string, unknown>[];
  custom_fields?: Record<string, unknown>;
}

export interface ClientFormTemplateSummary {
  id: string | number;
  name: string;
  description?: string;
  is_default?: boolean;
  is_deleted?: boolean;
  [key: string]: unknown;
}

export interface Subsidiary {
  id: string;
  name: string;
  legal_name: string;
  tax_id: string;
  fiscal_year_start_month: number;
  base_currency: string;
  is_active: boolean;
  tax_information?: unknown[];
  addresses?: unknown[];
  created_at: string;
  updated_at: string;
}

export interface SubsidiariesResponse {
  subsidiaries: Subsidiary[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class ClientOnboardingService {
  private readonly accountingUrl = environment.accountingUrl;
  private readonly http = inject(HttpClient);

  getDefaultFormTemplate(): Observable<ApiSuccessResponse<ClientTemplatePayload>> {
    const url = `${this.accountingUrl}/api/v1/client-form-templates/default`;
    return this.http.get<ApiSuccessResponse<ClientTemplatePayload>>(url, { withCredentials: true });
  }

  getFormTemplateById(templateId: string | number): Observable<ApiSuccessResponse<ClientTemplatePayload>> {
    const url = `${this.accountingUrl}/api/v1/client-form-templates/${templateId}`;
    return this.http.get<ApiSuccessResponse<ClientTemplatePayload>>(url, { withCredentials: true });
  }

  getFormTemplates(): Observable<ApiSuccessResponse<ClientFormTemplateSummary[]>> {
    const url = `${this.accountingUrl}/api/v1/client-form-templates`;
    return this.http.get<ApiSuccessResponse<ClientFormTemplateSummary[]>>(url, { withCredentials: true });
  }

  getClientById(clientId: string | number, includeSubsidiary: boolean = false): Observable<ApiSuccessResponse<ClientRecord>> {
    let url = `${this.accountingUrl}/api/v1/clients/${clientId}`;
    if (includeSubsidiary) {
      url += '?include_subsidiary=true';
    }
    return this.http.get<ApiSuccessResponse<ClientRecord>>(url, { withCredentials: true });
  }

  createClient(payload: ClientSubmissionPayload): Observable<ApiSuccessResponse<ClientRecord>> {
    const url = `${this.accountingUrl}/api/v1/clients`;
    return this.http.post<ApiSuccessResponse<ClientRecord>>(url, payload, { withCredentials: true });
  }

  updateClient(clientId: string | number, payload: ClientSubmissionPayload): Observable<ApiSuccessResponse<ClientRecord>> {
    const url = `${this.accountingUrl}/api/v1/clients/${clientId}`;
    return this.http.put<ApiSuccessResponse<ClientRecord>>(url, payload, { withCredentials: true });
  }

  getSubsidiaries(): Observable<ApiSuccessResponse<SubsidiariesResponse>> {
    const url = `${this.accountingUrl}/api/v1/subsidiaries/`;
    return this.http.get<ApiSuccessResponse<SubsidiariesResponse>>(url, { withCredentials: true });
  }

  /**
   * Get audit logs for a client
   * Endpoint: GET /api/v1/clients/:id/audit-logs
   */
  getAuditLogs(clientId: string | number, filterParams?: any): Observable<any> {
    let params = new HttpParams();
    
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== '') {
          params = params.set(key, filterParams[key].toString());
        }
      });
    }
    
    const url = `${this.accountingUrl}/api/v1/clients/${clientId}/audit-logs`;
    return this.http.get<any>(url, { 
      params,
      withCredentials: true 
    });
  }
}

